import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LOGO_SVG, doctorDisplay, buildWhatsAppBrandedMessage } from "../lib/clinic";
import { CERT_TYPES } from "../lib/certificateTypes";

export default function PrintCertificate() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/certificates/${cid}`);
        setCert(r.data.certificate);
      } catch (e) {
        console.warn("load cert failed:", e);
        toast.error("Could not load certificate");
      }
    })();
  }, [cid]);

  if (!cert) return <div className="p-8 text-sm text-slate-500">Loading certificate…</div>;
  const cfg = CERT_TYPES[cert.type];
  if (!cfg) return <div className="p-8 text-sm text-rose-700">Unknown certificate type: {cert.type}</div>;
  const p = cert.patient_snapshot || {};
  const f = cert.fields || {};
  const dateStr = new Date(cert.issued_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  const shareWA = () => {
    const lines = [cfg.intro(p), ""];
    cfg.sections.forEach((s) => {
      const rows = s.fields.filter((fld) => f[fld.name] || f[fld.name] === 0);
      if (rows.length === 0) return;
      lines.push(`*${s.title}*`);
      rows.forEach((fld) => lines.push(`• ${fld.label}: ${f[fld.name]}`));
      lines.push("");
    });
    lines.push(`Certificate No.: ${cert.certificate_id}`);
    lines.push(`Issued on: ${dateStr}`);
    const body = lines.join("\n");
    const msg = buildWhatsAppBrandedMessage({
      lang: "en",
      title: cfg.label,
      body,
      patientName: p.name,
      kind: "education", // reuse tagline; certificate is one-off
      dateStr,
    });
    window.open(`https://wa.me/${p.mobile ? p.mobile.replace(/\D/g, "") : ""}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50 print-shell">
      <Toaster position="top-right" richColors />
      <div className="print-toolbar sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="cert-print-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>
        <div className="text-sm text-slate-600">{cfg.label} · {p.name}</div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={shareWA} className="border-emerald-300 text-emerald-700" data-testid="cert-wa-btn">
            <MessageCircle size={14} className="mr-1" /> WhatsApp
          </Button>
          <Button size="sm" onClick={() => window.print()} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="cert-print-btn">
            <Printer size={14} className="mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page" data-testid="cert-print-page">
        <div className="p-8">
          <header className="flex justify-between items-start pb-4 border-b-2 border-slate-900">
            <div className="flex gap-4 items-start">
              <div className="w-16 h-20 flex-shrink-0">{LOGO_SVG}</div>
              <div>
                <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{CLINIC.qualifications}</div>
                <div className="text-[10px] text-slate-500">{CLINIC.regNo}</div>
              </div>
            </div>
            <div className="text-right text-[10px] text-slate-600 max-w-[220px] leading-snug">
              <div>{CLINIC.address}</div>
              {(CLINIC.phones || []).length > 0 && <div className="mt-1 tabular">{CLINIC.phones.join(" · ")}</div>}
            </div>
          </header>

          <section className="grid grid-cols-4 gap-2 text-[11px] mt-4 py-3 border-b border-slate-200">
            <div><span className="uppercase text-slate-500">Name:</span> <span className="font-medium">{p.name}</span></div>
            <div><span className="uppercase text-slate-500">Age/Sex:</span> {p.age} / {p.sex}</div>
            <div><span className="uppercase text-slate-500">Patient ID:</span> <span className="tabular">{cert.patient_id}</span></div>
            <div><span className="uppercase text-slate-500">Date:</span> <span className="tabular">{dateStr}</span></div>
          </section>

          <section className="mt-4 py-3 border-b border-slate-200">
            <div className="text-center">
              <div className="uppercase text-[10px] tracking-[0.25em] text-pink-600 font-semibold">Certificate of</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">{cfg.label}</div>
            </div>
          </section>

          <section className="mt-4 text-[12px] text-slate-800 leading-relaxed">
            <p>{cfg.intro(p)}</p>
          </section>

          {cfg.sections.map((s, si) => (
            <section key={si} className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{s.title}</div>
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-[11px]">
                  <tbody>
                    {s.fields.map((field) => {
                      const val = f[field.name];
                      if (!val && val !== 0) return null;
                      return (
                        <tr key={field.name} className="border-b border-slate-100 last:border-b-0">
                          <td className="align-top py-1.5 px-3 bg-slate-50 text-slate-600 w-[40%]">{field.label}</td>
                          <td className="align-top py-1.5 px-3 text-slate-900 whitespace-pre-wrap">{val}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <section className="py-3 border-t border-slate-200 mt-6 flex justify-between items-end rx-signature-block">
            <div className="text-[11px] text-slate-500">
              <div className="uppercase text-[9px] tracking-wider text-slate-500 font-semibold">Certificate No.</div>
              <div className="tabular font-medium text-slate-800">{cert.certificate_id}</div>
              <div className="mt-2 uppercase text-[9px] tracking-wider text-slate-500 font-semibold">Issued on</div>
              <div className="tabular">{dateStr}</div>
            </div>
            <div className="text-right max-w-[62%]">
              <div className="border-t border-slate-800 pt-1 mt-8 text-[11px]">
                <div className="font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[9.5px] text-slate-600 leading-snug mt-0.5">{CLINIC.qualifications}</div>
              </div>
            </div>
          </section>

          <footer className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed print-doctor-footer">
            This certificate is issued by <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong>, {CLINIC.name}, based on clinical examination on the date shown. Valid only for the purpose specified above. Please contact the clinic for verification.
          </footer>
        </div>

        <div className="print-marketing-footer border-t-2 border-pink-600 bg-gradient-to-r from-pink-50 via-white to-pink-50 text-slate-700">
          {/* MT-11C.4: services line comes from the ACTIVE tenant profile — nothing hardcoded. */}
          <div className={`grid ${CLINIC.specialties ? "grid-cols-3" : "grid-cols-2"} gap-3 px-4 py-2`}>
            {CLINIC.specialties && (
              <div>
                <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">Our Services</div>
                <div className="text-[10px] mt-0.5 leading-snug" data-testid="cert-footer-services">{CLINIC.specialties}</div>
              </div>
            )}
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">Evidence-Based Practice</div>
              <div className="text-[10px] mt-0.5 leading-snug">Certificates are issued strictly on clinical examination and current guidelines.</div>
            </div>
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">Consult With Confidence</div>
              <div className="text-[10px] mt-0.5 leading-snug">Follow-ups by appointment · Emergency: call reception first</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
